import { useNavigate } from 'react-router';
import { Avatar, Box, Center, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { DataGrid, Pagination, SortingArrow, type TSortValue } from '~/commonComponents';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import { firePermCandidateListCandidateClicked } from '~/posthogs/events';
import { EmptyState } from './EmptyState';
import { InviteColumnCell } from './InviteColumnCell';
import { ProcessStepCell } from './ProcessStepCell';
import { ShortlistCell } from './ShortlistCell';

interface ICandidateListTableV2Props {
  data: ICandidateListViewEntity[];
  isLoading: boolean;
  jobPostingId: string;
  officeId: string;
  pagination: { currentPage: number; totalPages: number };
  sorting: TSortValue;
  hideInviteColumn?: boolean;
  hideShortlistColumn?: boolean;
  onPageChange: (page: number) => void;
  onSortingChange?: (value: TSortValue | undefined) => void;
}

export const CandidateListTableV2 = ({
  data,
  isLoading,
  jobPostingId,
  officeId,
  sorting,
  onSortingChange,
  pagination,
  onPageChange,
  hideShortlistColumn = false,
  hideInviteColumn = false,
}: ICandidateListTableV2Props) => {
  const navigate = useNavigate();

  const columns = [
    {
      key: 'candidates',
      header: (
        <Typography as="span" bold color={colors.primary[500]} variant="body1" style={{ marginLeft: 86 }}>
          Candidates
        </Typography>
      ),
      width: '1fr',
      render: (item: ICandidateListViewEntity) => {
        const hasPendingCounts = item.pendingApplicationCount > 0;
        const shouldTrimCounts = item.pendingApplicationCount >= 10;

        return (
          <Row align="center" gap={5} pl={4}>
            <Box width="48px" height="48px" className="relative">
              <Avatar
                size="lg"
                src={item.professional.picture || undefined}
                alt={`${item.professional.name} profile picture`}
              />
              {hasPendingCounts && (
                <Center
                  data-testid="pending-badge"
                  height="20px"
                  width="20px"
                  className="absolute bg-error-500 rounded-full"
                  style={{ right: -10, top: -10 }}
                >
                  <Typography
                    as="span"
                    bold
                    color={colors.shades[0]}
                    size="caption"
                    style={shouldTrimCounts ? { marginLeft: 2.5 } : undefined}
                  >
                    {shouldTrimCounts ? '9+' : item.pendingApplicationCount}
                  </Typography>
                </Center>
              )}
            </Box>
            <Column>
              <Typography as="span" bold variant="body1" style={{ textDecoration: 'underline' }}>
                {item.professional.name}
              </Typography>
              <Typography as="span" color={colors.neutral[500]}>
                <Typography as="span" bold color={colors.neutral[500]}>
                  Experience
                </Typography>
                : {item.yearsOfExperience} years
              </Typography>
              <Typography as="span" color={colors.neutral[500]}>
                <Typography as="span" bold color={colors.neutral[500]}>
                  Applied on
                </Typography>
                : {item.createdAt}
              </Typography>
            </Column>
          </Row>
        );
      },
    },
    {
      key: 'processStep',
      header: (
        <SortingArrow
          name="status"
          sorting={sorting}
          style={{ justifyContent: 'flex-start' }}
          variant="primary"
          onChange={onSortingChange}
        >
          <Typography as="span" bold color={colors.primary[500]} variant="body1" style={{ marginLeft: 12 }}>
            Process Step
          </Typography>
        </SortingArrow>
      ),
      width: '30%',
      render: (item: ICandidateListViewEntity) => <ProcessStepCell status={item.officeStatus} />,
    },
    ...(!hideShortlistColumn
      ? [
          {
            key: 'shortlist',
            header: (
              <SortingArrow
                name="shortlist"
                sorting={sorting}
                variant="primary"
                onChange={onSortingChange}
              >
                <Typography as="span" bold color={colors.primary[500]} variant="body1">
                  Shortlist
                </Typography>
              </SortingArrow>
            ),
            width: '100px',
            render: (item: ICandidateListViewEntity) => (
              <ShortlistCell
                candidateId={item.id}
                isShortlisted={item.isShortlisted}
                officeStatus={item.officeStatus}
                jobPostingId={jobPostingId}
              />
            ),
          },
        ]
      : [
          {
            key: 'shortlist',
            header: '',
            width: '100px',
            render: () => null,
          },
        ]),
    ...(!hideInviteColumn
      ? [
          {
            key: 'invite',
            header: (
              <Typography as="span" bold color={colors.primary[500]} variant="body1" style={{ marginLeft: 12 }}>
                Invite
              </Typography>
            ),
            width: '80px',
            render: (item: ICandidateListViewEntity) => (
              <InviteColumnCell
                candidateId={item.id}
                jobPostingId={jobPostingId}
                officeId={officeId}
                officeStatus={item.officeStatus}
                professionalId={item.professional.id}
                professionalName={item.professional.name}
              />
            ),
          },
        ]
      : [
          {
            key: 'invite',
            header: '',
            width: '80px',
            render: () => null,
          },
        ]),
    {
      key: 'chevron',
      header: '',
      width: '42px',
      render: () => <OutlineChevronRightIcon />,
    },
  ];

  return (
    <DataGrid<ICandidateListViewEntity>
      EmptyComponent={<EmptyState />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={onPageChange}
        />
      }
      columns={columns}
      data={data}
      dataCy={{ rowItem: 'pj-candidate-list-item' }}
      dataTestId={{ rowItem: 'pj-candidate-list-item' }}
      isLoading={isLoading}
      showHeaderSeparator={false}
      showRowSeparator={false}
      getRowClassName={() => 'shadow-xs hover__shadow-sm'}
      getRowStyle={() => ({
        backgroundColor: colors.shades[0],
        borderRadius: 10,
        // DataGrid's body already puts a 4px gap between rows (shared with every other
        // grid), so 12 here is what lands the 16px card rhythm.
        marginBottom: 12,
        paddingBlock: 12,
      })}
      keyExtractor={(item) => item.id}
      onRowClick={(item) => {
        firePermCandidateListCandidateClicked({
          postingId: jobPostingId,
          professionalId: item.professional.id,
          candidateStatus: item.officeStatus,
        });

        navigate(`/permanent-jobs/${jobPostingId}/candidates/${item.professional.id}/overview`);
      }}
    />
  );
};
